<template>
  <v-card outlined class="section-card">
    <v-card-title class="section-header pa-4">
      <v-icon color="#023449" class="mr-2">mdi-shield-lock</v-icon>
      <span class="section-title">Controle de Acesso</span>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4">
      <!-- Radio Buttons para tipo de acesso -->
      <v-radio-group
          v-model="tipoAcesso"
          @change="handleTipoChange"
          :disabled="disabled"
          hide-details
          class="mb-4"
      >
        <v-radio
            value="publica"
            color="#023449"
            class="access-radio mb-2"
        >
          <template v-slot:label>
            <div class="d-flex align-center">
              <v-icon color="#023449" class="mr-2">mdi-earth</v-icon>
              <div>
                <div class="font-weight-bold text-body-1">Pública</div>
                <div class="text-caption text--secondary">Visível para todos os usuários do módulo</div>
              </div>
            </div>
          </template>
        </v-radio>

        <v-radio
            value="restrita"
            color="#023449"
            class="access-radio"
        >
          <template v-slot:label>
            <div class="d-flex align-center">
              <v-icon color="#023449" class="mr-2">mdi-lock</v-icon>
              <div>
                <div class="font-weight-bold text-body-1">Restrita</div>
                <div class="text-caption text--secondary">Visível apenas para grupos e usuários específicos</div>
              </div>
            </div>
          </template>
        </v-radio>
      </v-radio-group>

      <!-- Seção de Permissões (quando Restrita) -->
      <div v-if="tipoAcesso === 'restrita'">
        <!-- Alert de Aviso - Oculto quando tem seleção -->
        <v-expand-transition>
          <v-alert
              v-show="totalSelecionados === 0"
              type="warning"
              text
              dense
              class="mb-4"
              border="left"
          >
            <div class="d-flex align-center">
              <v-icon color="warning" class="mr-2">mdi-information</v-icon>
              <span>Selecione pelo menos <strong>um grupo ou usuário</strong> que terá permissão para visualizar esta normativa.</span>
            </div>
          </v-alert>
        </v-expand-transition>

        <!-- Validação Visual de Erro -->
        <v-expand-transition>
          <v-alert
              v-show="mostrarErroValidacao"
              type="error"
              text
              dense
              class="mb-4"
              border="left"
          >
            <div class="d-flex align-center">
              <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
              <span><strong>Atenção:</strong> Você deve selecionar pelo menos um grupo ou usuário para salvar uma normativa restrita.</span>
            </div>
          </v-alert>
        </v-expand-transition>

        <!-- Resumo de Seleção -->
        <div class="mb-4 d-flex align-center justify-space-between pa-3 selection-summary">
          <div class="d-flex align-center">
            <v-icon :color="totalSelecionados > 0 ? 'success' : 'grey'" class="mr-2">
              {{ totalSelecionados > 0 ? 'mdi-check-circle' : 'mdi-information' }}
            </v-icon>
            <span class="font-weight-medium">
              <span :class="totalSelecionados > 0 ? 'success--text' : 'text--secondary'">
                {{ totalSelecionados > 0 ? totalSelecionados : 'Nenhuma' }} seleç{{ totalSelecionados !== 1 ? 'ões' : 'ão' }}
              </span>
            </span>
          </div>
          <div v-if="totalSelecionados > 0" class="text-caption text--secondary">
            {{ gruposSelecionados.length }} grupo(s) • {{ usuariosSelecionados.length }} usuário(s)
          </div>
        </div>

        <v-row>
          <!-- Coluna: Grupos -->
          <v-col cols="12" md="6">
            <div class="permission-section">
              <div class="permission-section-header mb-3">
                <v-icon color="#023449" size="20" class="mr-2">mdi-account-group</v-icon>
                <span class="font-weight-bold">Grupos</span>
                <v-spacer></v-spacer>
                <v-chip
                    x-small
                    :color="gruposSelecionados.length > 0 ? '#e8f5e9' : '#f5f5f5'"
                    :text-color="gruposSelecionados.length > 0 ? '#2e7d32' : '#64748b'"
                >
                  {{ gruposSelecionados.length }}
                </v-chip>
              </div>

              <!-- Busca de Grupos -->
              <v-text-field
                  v-model="buscaGrupos"
                  label="Buscar grupos"
                  placeholder="Digite o nome do grupo..."
                  outlined
                  dense
                  clearable
                  hide-details
                  class="mb-3 search-field"
                  :disabled="disabled || carregando"
              >
                <template v-slot:prepend-inner>
                  <v-icon small color="#64748b">mdi-magnify</v-icon>
                </template>
              </v-text-field>

              <!-- Lista de Grupos -->
              <div class="permission-list">
                <v-progress-linear
                    v-if="carregando"
                    indeterminate
                    color="#023449"
                    class="mb-2"
                ></v-progress-linear>

                <div v-else-if="gruposFiltrados.length === 0" class="text-center pa-6 text--secondary">
                  <v-icon color="#cbd5e1" size="56">mdi-folder-open-outline</v-icon>
                  <div class="mt-2 text-body-2">Nenhum grupo encontrado</div>
                </div>

                <v-list v-else dense class="permission-list-items">
                  <v-list-item
                      v-for="grupo in gruposFiltrados"
                      :key="`grupo-${grupo.idgrupo}`"
                      @click="toggleGrupo(grupo.idgrupo)"
                      :disabled="disabled"
                      class="permission-item"
                  >
                    <v-list-item-action class="mr-3 my-0">
                      <v-checkbox
                          :input-value="isGrupoSelecionado(grupo.idgrupo)"
                          color="#023449"
                          hide-details
                          :disabled="disabled"
                          @click.stop="toggleGrupo(grupo.idgrupo)"
                      ></v-checkbox>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium text-body-2">
                        <v-icon small color="#64748b" class="mr-1">mdi-account-group</v-icon>
                        {{ grupo.grupo }}
                      </v-list-item-title>
                    </v-list-item-content>

                    <v-list-item-action v-if="isGrupoSelecionado(grupo.idgrupo)" class="my-0">
                      <v-icon small color="success">mdi-check-circle</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </div>
            </div>
          </v-col>

          <!-- Coluna: Usuários -->
          <v-col cols="12" md="6">
            <div class="permission-section">
              <div class="permission-section-header mb-3">
                <v-icon color="#023449" size="20" class="mr-2">mdi-account</v-icon>
                <span class="font-weight-bold">Usuários Específicos</span>
                <v-spacer></v-spacer>
                <v-chip
                    x-small
                    :color="usuariosSelecionados.length > 0 ? '#e8f5e9' : '#f5f5f5'"
                    :text-color="usuariosSelecionados.length > 0 ? '#2e7d32' : '#64748b'"
                >
                  {{ usuariosSelecionados.length }}
                </v-chip>
              </div>

              <!-- Busca de Usuários -->
              <v-text-field
                  v-model="buscaUsuarios"
                  label="Buscar usuários"
                  placeholder="Digite o nome do usuário..."
                  outlined
                  dense
                  clearable
                  hide-details
                  class="mb-3 search-field"
                  :disabled="disabled || carregando"
              >
                <template v-slot:prepend-inner>
                  <v-icon small color="#64748b">mdi-magnify</v-icon>
                </template>
              </v-text-field>

              <!-- Filtros -->
              <v-row dense class="mb-3">
                <v-col cols="12" sm="6">
                  <v-select
                      v-model="empresaFiltro"
                      :items="empresasDisponiveis"
                      item-text="empresa"
                      item-value="idempresa"
                      label="Empresa"
                      outlined
                      dense
                      clearable
                      hide-details
                      class="filter-field"
                      :disabled="disabled || carregando"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small color="#64748b">mdi-domain</v-icon>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                      v-model="grupoFiltro"
                      :items="gruposDisponiveis"
                      item-text="grupo"
                      item-value="idgrupo"
                      label="Grupo"
                      outlined
                      dense
                      clearable
                      hide-details
                      class="filter-field"
                      :disabled="disabled || carregando"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon small color="#64748b">mdi-account-group</v-icon>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- Lista de Usuários -->
              <div class="permission-list">
                <v-progress-linear
                    v-if="carregando"
                    indeterminate
                    color="#023449"
                    class="mb-2"
                ></v-progress-linear>

                <div v-else-if="usuariosFiltrados.length === 0" class="text-center pa-6 text--secondary">
                  <v-icon color="#cbd5e1" size="56">mdi-account-off-outline</v-icon>
                  <div class="mt-2 text-body-2">Nenhum usuário encontrado</div>
                </div>

                <v-list v-else dense class="permission-list-items">
                  <v-list-item
                      v-for="usuario in usuariosFiltrados"
                      :key="`usuario-${usuario.idusuario}`"
                      @click="toggleUsuario(usuario.idusuario)"
                      :disabled="disabled"
                      class="permission-item"
                  >
                    <v-list-item-action class="mr-3 my-0">
                      <v-checkbox
                          :input-value="isUsuarioSelecionado(usuario.idusuario)"
                          color="#023449"
                          hide-details
                          :disabled="disabled"
                          @click.stop="toggleUsuario(usuario.idusuario)"
                      ></v-checkbox>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium text-body-2">
                        {{ usuario.nome }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption mt-1">
                        <v-icon x-small color="#94a3b8" class="mr-1">mdi-domain</v-icon>
                        {{ usuario.empresa }}
                        <span class="mx-1">•</span>
                        <v-icon x-small color="#94a3b8" class="mr-1">mdi-account-group</v-icon>
                        {{ usuario.grupo }}
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action v-if="isUsuarioSelecionado(usuario.idusuario)" class="my-0">
                      <v-icon small color="success">mdi-check-circle</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'

export default {
  name: 'NormativaPermissoes',

  props: {
    value: {
      type: Object,
      default: () => ({
        publica: 'T',
        ids_grupos: [],
        ids_usuarios: []
      })
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      carregando: false,
      usuarios: [],

      buscaGrupos: '',
      buscaUsuarios: '',
      empresaFiltro: null,
      grupoFiltro: null,

      gruposSelecionados: [],
      usuariosSelecionados: [],

      mostrarErroValidacao: false
    }
  },

  computed: {
    ...mapState(['backendUrl']),

    tipoAcesso: {
      get() {
        return this.value.publica === 'T' ? 'publica' : 'restrita'
      },
      set(val) {
        this.emitChange({
          publica: val === 'publica' ? 'T' : 'F',
          ids_grupos: val === 'restrita' ? this.gruposSelecionados : [],
          ids_usuarios: val === 'restrita' ? this.usuariosSelecionados : []
        })
      }
    },

    // Extrair grupos únicos dos usuários
    gruposDisponiveis() {
      const grupos = []
      const idsVistos = new Set()

      this.usuarios.forEach(usuario => {
        if (!idsVistos.has(usuario.idgrupo)) {
          idsVistos.add(usuario.idgrupo)
          grupos.push({
            idgrupo: usuario.idgrupo,
            grupo: usuario.grupo
          })
        }
      })

      return grupos.sort((a, b) => a.grupo.localeCompare(b.grupo))
    },

    // Extrair empresas únicas dos usuários
    empresasDisponiveis() {
      const empresas = []
      const idsVistos = new Set()

      this.usuarios.forEach(usuario => {
        if (!idsVistos.has(usuario.idempresa)) {
          idsVistos.add(usuario.idempresa)
          empresas.push({
            idempresa: usuario.idempresa,
            empresa: usuario.empresa
          })
        }
      })

      return empresas.sort((a, b) => a.empresa.localeCompare(b.empresa))
    },

    gruposFiltrados() {
      let grupos = [...this.gruposDisponiveis]

      // Filtro por busca
      if (this.buscaGrupos) {
        const termo = this.buscaGrupos.toLowerCase()
        grupos = grupos.filter(g =>
            g.grupo.toLowerCase().includes(termo)
        )
      }

      return grupos
    },

    usuariosFiltrados() {
      let usuarios = [...this.usuarios]

      // Filtro por busca
      if (this.buscaUsuarios) {
        const termo = this.buscaUsuarios.toLowerCase()
        usuarios = usuarios.filter(u =>
            u.nome.toLowerCase().includes(termo)
        )
      }

      // Filtro por empresa
      if (this.empresaFiltro) {
        usuarios = usuarios.filter(u => u.idempresa === this.empresaFiltro)
      }

      // Filtro por grupo
      if (this.grupoFiltro) {
        usuarios = usuarios.filter(u => u.idgrupo === this.grupoFiltro)
      }

      return usuarios.sort((a, b) => a.nome.localeCompare(b.nome))
    },

    totalSelecionados() {
      return this.gruposSelecionados.length + this.usuariosSelecionados.length
    }
  },

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.gruposSelecionados = val.ids_grupos || []
          this.usuariosSelecionados = val.ids_usuarios || []
        }
      }
    },

    totalSelecionados(val) {
      // Ocultar erro quando selecionar algo
      if (val > 0) {
        this.mostrarErroValidacao = false
      }
    }
  },

  mounted() {
    this.carregarUsuarios()
  },

  methods: {
    async carregarUsuarios() {
      this.carregando = true

      try {
        const response = await axios.post(`${this.backendUrl}normativa/listar/usuarios`)
        this.usuarios = response.data || []
      } catch (error) {
        console.error('Erro ao carregar usuários:', error)
        this.$emit('erro', 'Erro ao carregar usuários e grupos')
      } finally {
        this.carregando = false
      }
    },

    handleTipoChange() {
      if (this.tipoAcesso === 'publica') {
        // Ao mudar para pública, limpar seleções
        this.gruposSelecionados = []
        this.usuariosSelecionados = []
        this.mostrarErroValidacao = false
      }
    },

    toggleGrupo(idgrupo) {
      if (this.disabled) return

      const index = this.gruposSelecionados.indexOf(idgrupo)

      if (index > -1) {
        this.gruposSelecionados.splice(index, 1)
      } else {
        this.gruposSelecionados.push(idgrupo)
      }

      this.emitChange({
        publica: 'F',
        ids_grupos: this.gruposSelecionados,
        ids_usuarios: this.usuariosSelecionados
      })
    },

    toggleUsuario(idusuario) {
      if (this.disabled) return

      const index = this.usuariosSelecionados.indexOf(idusuario)

      if (index > -1) {
        this.usuariosSelecionados.splice(index, 1)
      } else {
        this.usuariosSelecionados.push(idusuario)
      }

      this.emitChange({
        publica: 'F',
        ids_grupos: this.gruposSelecionados,
        ids_usuarios: this.usuariosSelecionados
      })
    },

    isGrupoSelecionado(idgrupo) {
      return this.gruposSelecionados.includes(idgrupo)
    },

    isUsuarioSelecionado(idusuario) {
      return this.usuariosSelecionados.includes(idusuario)
    },

    emitChange(value) {
      this.$emit('input', value)
    },

    validar() {
      // Se for restrita e não tiver nenhum selecionado
      if (this.tipoAcesso === 'restrita' && this.totalSelecionados === 0) {
        this.mostrarErroValidacao = true
        return false
      }

      this.mostrarErroValidacao = false
      return true
    }
  }
}
</script>

<style scoped>
.section-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.04);
}

.section-header {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #023449;
  letter-spacing: -0.01em;
}

/* Radio Buttons */
.access-radio {
  padding: 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.access-radio:hover {
  border-color: #cbd5e1;
  background-color: #f8fafc;
}

.v-item--active .access-radio {
  border-color: #023449;
  background-color: #f0f9ff;
}

/* Resumo de Seleção */
.selection-summary {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* Seção de Permissões */
.permission-section {
  height: 100%;
}

.permission-section-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* Campos de Busca e Filtro */
.search-field >>> .v-input__control,
.filter-field >>> .v-input__control {
  background-color: white;
}

.search-field >>> fieldset,
.filter-field >>> fieldset {
  border-color: #e2e8f0 !important;
}

.search-field:hover >>> fieldset,
.filter-field:hover >>> fieldset {
  border-color: #cbd5e1 !important;
}

/* Lista de Permissões */
.permission-list {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background-color: white;
  max-height: 420px;
  overflow-y: auto;
}

.permission-list-items {
  background-color: transparent;
}

.permission-item {
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  cursor: pointer;
}

.permission-item:last-child {
  border-bottom: none;
}

.permission-item:hover {
  background-color: #f8fafc;
}

.permission-item >>> .v-list-item__action {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

/* Scrollbar */
.permission-list::-webkit-scrollbar {
  width: 8px;
}

.permission-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.permission-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.permission-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Animações */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}
</style>